import type { FC } from "react";

import {
  CupIcon,
  DocumentBadgeCheckIcon,
  FaceSquareSadIcon,
} from "@repo/edtech-assets";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  ParsedHtmlView,
  SanitizeOptionsMode,
} from "@repo/edtech-ui-kit";
import clsx from "clsx";

import {
  getDescription,
  getRemainingAttemptsText,
  parseAttempts,
} from "../../../../../../lib";
import { Lesson, LessonStatus } from "../../../../../../model";
import {
  getConfig,
  OriginAsset,
  t,
} from "../../../../../../origins";
import { ShowResultsModal } from "../show-results-modal/ShowResultsModal";

import st from "./MainTestCover.module.scss";

const FailTest = ({
  onClickNewTest,
  onClickNextLesson,
  testResult,
  canRetake,
  isShowReport,
  lessonUid,
  isRetakeLoading,
  remainingAttempts,
  isFinished,
  isLastLesson,
  isNextLessonDisabled,
  isTestExpired,
}: {
  onClickNewTest: () => void,
  onClickNextLesson: () => void,
  testResult?: string,
  canRetake: boolean,
  isShowReport: boolean,
  lessonUid: string,
  isRetakeLoading: boolean,
  remainingAttempts?: number,
  isFinished: boolean,
  isLastLesson: boolean,
  isNextLessonDisabled: boolean,
  isTestExpired: boolean,
}) => {
  const title = canRetake ?
    "Тест не пройден. Попробуйте пройти его снова — вы на верном пути!" :
    "Тест не пройден";

  return (
    <div className={st.wrap}>
      <FaceSquareSadIcon className={st.icon} />
      <h2 className={st.title}>{title}</h2>
      {
        isTestExpired && (
          <p className={st.sub_description}>Тест завершён после истечения времени на прохождение</p>
        )
      }
      {
        testResult && (
          <p className={st.sub_description}>Ваш балл: {testResult}</p>
        )
      }
      <div className={st.actions}>
        {
          isShowReport && (
            <ShowResultsModal
              lessonUid={lessonUid}
              Trigger={
                (
                  <Button
                    className={clsx(st.btn, st.action_btn)}
                    size={ButtonSize.Medium}
                    variant={ButtonVariant.Outline}
                  >
                    Показать отчет
                  </Button>
                )
              }
            />
          )
        }
        {
          canRetake && (
            <Button
              className={clsx(st.btn)}
              disabled={isRetakeLoading}
              size={ButtonSize.Medium}
              variant={ButtonVariant.Outline}
              onClick={onClickNewTest}
            >
              Пройти повторно
            </Button>
          )
        }
        {
          isFinished && !isLastLesson && (
            <Button
              className={clsx(st.btn)}
              disabled={isNextLessonDisabled}
              size={ButtonSize.Medium}
              variant={ButtonVariant.Primary}
              onClick={onClickNextLesson}
            >
              Следующий урок
            </Button>
          )
        }
      </div>
      {
        remainingAttempts != null && (
          <p className={st.attempts_left}>
            {getRemainingAttemptsText(remainingAttempts)}
          </p>
        )
      }
    </div>
  );
};

const NewTest = ({
  onClickStartTest,
  title,
  isSurvey,
  description,
}: {
  onClickStartTest: () => void,
  title: string,
  isSurvey: boolean,
  description?: string,
}) => {
  const { isImageWithModal } = getConfig();

  return (
    <div className={st.wrap}>
      <OriginAsset
        assetKey="mainTestCoverNotStartedIcon"
        className={st.icon}
      />
      <h2 className={st.title}>{title}</h2>
      <ParsedHtmlView
        className={st.description}
        enableImageModal={isImageWithModal}
        html={getDescription(description, isSurvey)}
        mode={SanitizeOptionsMode.html}
      />
      <Button
        className={clsx(st.btn, st.action_btn)}
        size={ButtonSize.Medium}
        variant={ButtonVariant.Primary}
        onClick={onClickStartTest}
      >
        {t("mainTestCover.startButton")}
      </Button>
    </div>
  );
};

const InProgressTest = ({
  onClickContinueTest,
  onClickNewTest,
  isSurvey,
  canRetake,
  isRetakeLoading,
}: {
  onClickContinueTest: () => void,
  onClickNewTest: () => void,
  isSurvey: boolean,
  canRetake: boolean,
  isRetakeLoading: boolean,
}) => {
  return (
    <div className={st.wrap}>
      <OriginAsset
        assetKey="mainTestCoverInProgressIcon"
        className={st.icon}
      />
      <h2 className={st.title}>Вы не закончили {
        isSurvey ?
          "опрос" :
          t("mainTestCover.notFinishedSuffix")
      }
      </h2>
      <p className={st.description}>
        Продолжите с&nbsp;того вопроса, на&nbsp;котором остановились, или начните
        сначала
      </p>
      <div className={st.actions}>
        {
          canRetake && (
            <Button
              className={st.btn}
              disabled={isRetakeLoading}
              size={ButtonSize.Medium}
              variant={ButtonVariant.Outline}
              onClick={onClickNewTest}
            >
              Начать заново
            </Button>
          )
        }
        <Button
          className={st.btn}
          size={ButtonSize.Medium}
          variant={ButtonVariant.Primary}
          onClick={onClickContinueTest}
        >
          Продолжить
        </Button>
      </div>
    </div>
  );
};

const PassedTest = ({
  isLastLesson,
  isNextLessonDisabled,
  onClickRetakeTest,
  onClickNextLesson,
  onClickFinishCourse,
  testResult,
  canRetake,
  isCoursePassed,
  hasSuccessScreen,
  isShowReport,
  lessonUid,
  isRetakeLoading,
  remainingAttempts,
  isTestExpired,
}: {
  isLastLesson: boolean,
  isNextLessonDisabled: boolean,
  onClickRetakeTest: () => void,
  onClickNextLesson: () => void,
  onClickFinishCourse: () => void,
  testResult?: string,
  canRetake: boolean,
  isCoursePassed: boolean,
  hasSuccessScreen: boolean,
  isShowReport: boolean,
  lessonUid: string,
  isRetakeLoading: boolean,
  remainingAttempts?: number,
  isTestExpired: boolean,
}) => {
  // todo make normal
  const showCertificateButton = isLastLesson && isCoursePassed && hasSuccessScreen;
  return (
    <div className={st.wrap}>
      <CupIcon className={st.icon} />
      <h2 className={st.title}>
        Поздравляем!
        {" "}
        <br />
        Вы успешно завершили тестирование
      </h2>
      {
        isTestExpired && (
          <p className={st.sub_description}>Тест завершён после истечения времени на прохождение</p>
        )
      }
      {
        testResult && (
          <p className={st.sub_description}>Ваш балл: {testResult}</p>
        )
      }
      <div className={st.actions}>
        {
          canRetake && (
            <Button
              className={st.btn}
              disabled={isRetakeLoading}
              size={ButtonSize.Medium}
              variant={ButtonVariant.Outline}
              onClick={onClickRetakeTest}
            >
              Пройти повторно
            </Button>
          )
        }
        {
          !isLastLesson && (
            <Button
              className={st.btn}
              disabled={isNextLessonDisabled}
              size={ButtonSize.Medium}
              variant={ButtonVariant.Primary}
              onClick={onClickNextLesson}
            >
              Следующий урок
            </Button>
          )
        }
        {
          showCertificateButton && (
            <Button
              className={st.btn}
              size={ButtonSize.Medium}
              variant={ButtonVariant.Primary}
              onClick={onClickFinishCourse}
            >
              Скачать сертификат
            </Button>
          )
        }
        {
          isShowReport && (
            <ShowResultsModal
              lessonUid={lessonUid}
              Trigger={
                (
                  <Button
                    className={st.btn}
                    size={ButtonSize.Medium}
                    variant={ButtonVariant.Outline}
                  >
                    Показать отчет
                  </Button>
                )
              }
            />
          )
        }
      </div>
      {
        remainingAttempts != null && (
          <p className={st.attempts_left}>
            {getRemainingAttemptsText(remainingAttempts)}
          </p>
        )
      }
    </div>
  );
};

const SurveyThanks = ({
  isLastLesson,
  onClickNextLesson,
  onClickNewTest,
  isNextLessonDisabled,
  canRetake,
  isRetakeLoading,
}: {
  isLastLesson: boolean,
  onClickNextLesson: () => void,
  onClickNewTest: () => void,
  isNextLessonDisabled: boolean,
  canRetake: boolean,
  isRetakeLoading: boolean,
}) => {
  return (
    <div className={st.wrap}>
      <DocumentBadgeCheckIcon className={st.icon} />
      <h2 className={st.title}>Ответы отправлены</h2>
      {/* TODO maybe need in future */}
      {/*<p className={st.description}>*/}
      {/*  Она будет использована для улучшения качества учебного материала*/}
      {/*</p>*/}
      <div className={st.actions}>
        {
          canRetake && (
            <Button
              className={st.btn}
              disabled={isRetakeLoading}
              size={ButtonSize.Medium}
              variant={ButtonVariant.Outline}
              onClick={onClickNewTest}
            >
              Пройти заново
            </Button>
          )
        }
        {
          !isLastLesson && (
            <Button
              className={st.btn}
              disabled={isNextLessonDisabled}
              size={ButtonSize.Medium}
              variant={ButtonVariant.Primary}
              onClick={onClickNextLesson}
            >
              Следующий урок
            </Button>
          )
        }
      </div>
    </div>
  );
};

const QueuedTest = ({
  isLastLesson,
  onClickNextLesson,
  onClickNewTest,
  isNextLessonDisabled,
  isSurvey,
  canRetake,
  isRetakeLoading,
}: {
  isLastLesson: boolean,
  onClickNextLesson: () => void,
  onClickNewTest: () => void,
  isNextLessonDisabled: boolean,
  isSurvey: boolean,
  canRetake: boolean,
  isRetakeLoading: boolean,
}) => {
  const { nextLessonButtonVariant } = getConfig();

  if (isSurvey) {
    return (
      <SurveyThanks
        canRetake={canRetake}
        isLastLesson={isLastLesson}
        isNextLessonDisabled={isNextLessonDisabled}
        isRetakeLoading={isRetakeLoading}
        onClickNewTest={onClickNewTest}
        onClickNextLesson={onClickNextLesson}
      />
    );
  }

  return (
    <div className={st.wrap}>
      <OriginAsset
        assetKey="mainTestCoverQueuedIcon"
        className={st.icon}
      />
      <h2 className={st.title}>
        {t("mainTestCover.queuedTitle")}
      </h2>
      <p className={st.description}>
        {t("mainTestCover.queuedDescription")}
      </p>
      <Button
        className={clsx(st.btn, st.action_btn)}
        disabled={isNextLessonDisabled}
        size={ButtonSize.Medium}
        variant={nextLessonButtonVariant as ButtonVariant}
        onClick={onClickNextLesson}
      >
        {t("mainTestCover.queuedNextButton")}
      </Button>
    </div>
  );
};

type Props = {
  lesson: Lesson,
  isLastLesson: boolean,
  isNextLessonDisabled: boolean,
  onClickStartTest: () => void,
  onClickNewTest: () => void,
  onClickContinueTest: () => void,
  onClickNextLesson: () => void,
  onClickFinishCourse: () => void,
  testStatus: LessonStatus,
  testResult?: string,
  isSurvey: boolean,
  isCoursePassed: boolean,
  hasSuccessScreen: boolean,
  isShowReport: boolean,
  isRetakeLoading: boolean,
  isTestExpired: boolean,
};

export const MainTestCover: FC<Props> = ({
  lesson,
  isLastLesson,
  isNextLessonDisabled = false,
  onClickStartTest,
  onClickNewTest,
  onClickContinueTest,
  onClickNextLesson,
  onClickFinishCourse,
  testStatus,
  testResult,
  isSurvey,
  isCoursePassed,
  hasSuccessScreen,
  isShowReport,
  isRetakeLoading,
  isTestExpired,
}) => {
  const {
    title,
    description,
    attemptsCount: rawAttemptsCount,
    attemptsNumber: rawAttemptsNumber,
    uid: lessonUid,
  } = lesson;

  const attemptsUsed = parseAttempts(rawAttemptsCount) ?? 0;
  const parsedAttemptsLimit = parseAttempts(rawAttemptsNumber);

  const attemptsLimit =
    parsedAttemptsLimit != null && parsedAttemptsLimit > 0 ?
      parsedAttemptsLimit :
      undefined;

  const canRetake =
    attemptsLimit == null || attemptsUsed < attemptsLimit;

  const remainingAttempts =
    attemptsLimit != null ?
      Math.max(0, attemptsLimit - attemptsUsed) :
      undefined;

  switch (testStatus) {
    case LessonStatus.Passed: {
      if (isSurvey) {
        return (
          <SurveyThanks
            canRetake={canRetake}
            isLastLesson={isLastLesson}
            isNextLessonDisabled={isNextLessonDisabled}
            isRetakeLoading={isRetakeLoading}
            onClickNewTest={onClickNewTest}
            onClickNextLesson={onClickNextLesson}
          />
        );
      }
      return (
        <PassedTest
          canRetake={canRetake}
          hasSuccessScreen={hasSuccessScreen}
          isCoursePassed={isCoursePassed}
          isLastLesson={isLastLesson}
          isNextLessonDisabled={isNextLessonDisabled}
          isRetakeLoading={isRetakeLoading}
          isShowReport={isShowReport}
          isTestExpired={isTestExpired}
          lessonUid={lessonUid}
          remainingAttempts={remainingAttempts}
          testResult={testResult}
          onClickFinishCourse={onClickFinishCourse}
          onClickNextLesson={onClickNextLesson}
          onClickRetakeTest={onClickNewTest}
        />
      );
    }
    case LessonStatus.Failed: {
      return (
        <FailTest
          canRetake={canRetake}
          isFinished={lesson.isFinished === true}
          isLastLesson={isLastLesson}
          isNextLessonDisabled={isNextLessonDisabled}
          isRetakeLoading={isRetakeLoading}
          isShowReport={isShowReport}
          isTestExpired={isTestExpired}
          lessonUid={lessonUid}
          remainingAttempts={remainingAttempts}
          testResult={testResult}
          onClickNewTest={onClickNewTest}
          onClickNextLesson={onClickNextLesson}
        />
      );
    }
    case LessonStatus.NotStarted: {
      return (
        <NewTest
          description={description}
          isSurvey={isSurvey}
          title={title!}
          onClickStartTest={onClickStartTest}
        />
      );
    }
    case LessonStatus.InProgress: {
      return (
        <InProgressTest
          canRetake={canRetake}
          isRetakeLoading={isRetakeLoading}
          isSurvey={isSurvey}
          onClickContinueTest={onClickContinueTest}
          onClickNewTest={onClickNewTest}
        />
      );
    }
    case LessonStatus.Queued: {
      return (
        <QueuedTest
          canRetake={canRetake}
          isLastLesson={isLastLesson}
          isNextLessonDisabled={isNextLessonDisabled}
          isRetakeLoading={isRetakeLoading}
          isSurvey={isSurvey}
          onClickNewTest={onClickNewTest}
          onClickNextLesson={onClickNextLesson}
        />
      );
    }
    default: {
      return (
        <NewTest
          description={description}
          isSurvey={isSurvey}
          title={title!}
          onClickStartTest={onClickStartTest}
        />
      );
    }
  }
};
